import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { HiEnvelope, HiPrinter } from "react-icons/hi2";
import paykitLogo from "../../../../assets/img/paykit _logo1.webp";
import {
  formatDateTimeToLocale,
  formatKES,
  getDisplayValue,
} from "../../../../utils/transactionutils";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomModal from "../../../ui/CustomModal/CustomModal";
import {
  SummaryRow,
  TransactionDetailsCard,
} from "../../../shared/result/ResultPrimitives";

function composeReceiptEmail({
  transactionReference,
  transactionType,
  businessName,
  status,
  mpesaReference,
  verifiedAlias,
  aliasLabel,
  accountNumber,
  transactionFee,
  total,
  newBalance,
  createdDate,
}) {
  const subject = `Payment Receipt${transactionReference ? ` - ${transactionReference}` : ""}`;

  const lines = [
    "PAYMENT RECEIPT",
    "===============",
    "",
    `Date:               ${formatDateTimeToLocale(createdDate)}`,
    `Transaction ID:     ${getDisplayValue(transactionReference)}`,
    `Transaction Type:   ${getDisplayValue(transactionType)}`,
    `Recipient:          ${getDisplayValue(businessName)}`,
    `Status:             ${getDisplayValue(status)}`,
  ];

  if (mpesaReference) {
    lines.push(`Merchant Reference: ${getDisplayValue(mpesaReference)}`);
  }

  lines.push(
    `${aliasLabel ?? "Reference"}:${" ".repeat(Math.max(1, 20 - (aliasLabel?.length ?? 9)))}${getDisplayValue(verifiedAlias)}`,
  );

  if (accountNumber) {
    lines.push(`Account Number:     ${getDisplayValue(accountNumber)}`);
  }

  lines.push(
    "",
    `Transaction Fee:    ${formatKES(transactionFee)}`,
    `Total Deducted:     ${formatKES(total)}`,
    `New Wallet Balance: ${formatKES(newBalance)}`,
  );

  return { subject, body: lines.join("\n") };
}

export function ReceiptModal({
  opened,
  onClose,
  transactionReference,
  transactionType,
  businessName,
  status,
  mpesaReference,
  verifiedAlias,
  aliasLabel,
  accountNumber,
  transactionFee,
  total,
  newBalance,
  createdDate,
}) {
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: transactionReference
      ? `Receipt-${transactionReference}`
      : "Receipt",
  });

  const handleShare = () => {
    const { subject, body } = composeReceiptEmail({
      transactionReference,
      transactionType,
      businessName,
      status,
      mpesaReference,
      verifiedAlias,
      aliasLabel,
      accountNumber,
      transactionFee,
      total,
      newBalance,
      createdDate,
    });
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      styles={{
        content: { maxWidth: "calc(100vw - 2rem)" },
        body: { padding: 0 },
      }}
    >
      <div style={{ maxHeight: "calc(90dvh - 160px)", overflowY: "auto" }}>
        <div
          ref={contentRef}
          className="flex flex-col items-center gap-5 px-4 pt-4 sm:px-6 sm:pt-6 print:p-8"
        >
          <img
            src={paykitLogo}
            alt="Paykit"
            className="h-10 sm:h-12 object-contain"
          />

          {createdDate && (
            <p className="text-xs sm:text-sm text-center text-gray-400 -mt-2">
              {formatDateTimeToLocale(createdDate)}
            </p>
          )}

          <TransactionDetailsCard>
            {transactionReference && (
              <SummaryRow
                label="Transaction ID"
                value={transactionReference}
                stacked
              />
            )}
            {transactionType && (
              <SummaryRow label="Transaction Type" value={transactionType} />
            )}
            {businessName && (
              <SummaryRow label="Recipient" value={businessName} />
            )}
            {status && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">Status</span>
                <CustomBadge color="green" variant="light" size="sm">
                  {status}
                </CustomBadge>
              </div>
            )}
            {mpesaReference && (
              <SummaryRow
                label="Merchant Reference"
                value={mpesaReference}
                stacked
              />
            )}
            <SummaryRow label={aliasLabel} value={verifiedAlias ?? "—"} />
            {accountNumber && (
              <SummaryRow label="Account Number" value={accountNumber} />
            )}
            <SummaryRow
              label="Transaction Fee"
              value={formatKES(transactionFee)}
            />
            <SummaryRow label="Total Deducted" value={formatKES(total)} bold />
            <SummaryRow
              label="New Wallet Balance"
              value={formatKES(newBalance)}
            />
          </TransactionDetailsCard>

          <div className="w-full flex gap-2 pb-4 sm:pb-6">
            <CustomButton
              flex={1}
              radius="md"
              leftSection={<HiEnvelope style={{ fontSize: 14 }} />}
              onClick={handleShare}
            >
              Share Receipt
            </CustomButton>
            <CustomButton
              flex={1}
              variant="outline"
              color="gray"
              radius="md"
              leftSection={<HiPrinter style={{ fontSize: 14 }} />}
              onClick={handlePrint}
            >
              Print Receipt
            </CustomButton>
          </div>
        </div>
      </div>
    </CustomModal>
  );
}
